import Debug from 'debug';
import { PracticeQuestion, PracticeCurrentQuestion } from './practice-question'
import { QuizQuestionsState, QuizNavigationState, NavigationState, NavigationOptions,
         QuizStateMachinePhaseState, QuizQuestionTimeState,
  InputNavigation, InputQuestionTimes, QuizPlayerInputQuestionStates } from '@/app/quiz-player/api';
import { QuestionReference } from '@/app/quiz';
import { PracticeStateMachinePhase } from '../states';

const debug = Debug('practice-state-machine-data')

interface Score {
  min: number
  max: number
  raw: number
}

interface AdaptivityState {
  allPracticeQuestions: QuestionReference[]
  level: Level
}

interface Level {
  before: number
  after: number
}

interface CommonParameters {
  sessionId: string
  questions: PracticeQuestion[]
  adaptivity: {
    references: QuestionReference[]
    level: number
  }
}

interface State {
  questionStates: QuizPlayerInputQuestionStates
  navigation: InputNavigation
  questionTimes: InputQuestionTimes
}

export class PracticeStateMachineData implements NavigationState {

  private sessionId: string
  private navigation: QuizNavigationState
  private questionsState: QuizQuestionsState
  private phaseState: QuizStateMachinePhaseState
  private adaptivity: AdaptivityState
  private questionTimes: QuizQuestionTimeState
  private currentError: unknown

  constructor() {
    this.initialize()
  }

  initialize() {
    this.sessionId = ''
    this.navigation = new QuizNavigationState(0)
    this.questionsState = new QuizQuestionsState([], { questionsDisabled: true })
    this.phaseState = new QuizStateMachinePhaseState()
    this.adaptivity = {
      allPracticeQuestions: [],
      level: { before: 0, after: 0 }
    }
    this.questionTimes = new QuizQuestionTimeState(0)
  }

  updatePhase(phase: PracticeStateMachinePhase) {
    this.phaseState.updatePhase(phase)
  }

  started(options: CommonParameters) {

    debug('started', options)
    this.createState(options)
    this.enableQuestion(0)
    this.updateCurrentQuestion(0, { updateLastIndex: true })
  }

  continued(options: CommonParameters, { questionStates, navigation, questionTimes }: State) {
    debug('continued', options, questionStates, navigation, questionTimes)
    this.createState(options)

    this.questionsState.initializeQuestionStates(questionStates)
    this.navigation.load(navigation)
    this.questionTimes.load(questionTimes)
    this.enableQuestionsAfterContinue(navigation.lastIndex)
  }

  private enableQuestionsAfterContinue(lastIndex: number) {
    for (let index = 0; index <= lastIndex; index++) {
      this.questionsState.enabledQuestion(index)
    }
    
    const hasMoreQuestions = lastIndex + 1 < this.questionsState.questionCount
    const isLastEvaluated = this.questionsState.isEvaluated(lastIndex)
    if (isLastEvaluated && hasMoreQuestions) {
      this.questionsState.enabledQuestion(lastIndex + 1)
    }
  }

  private createState({ sessionId, questions, adaptivity }: CommonParameters) {
    this.sessionId = sessionId
    this.questionsState = new QuizQuestionsState(questions, { questionsDisabled: true })
    this.navigation = new QuizNavigationState(questions.length)
    this.adaptivity = {
      allPracticeQuestions: adaptivity.references,
      level: { before: adaptivity.level, after: adaptivity.level }
    }
    this.questionTimes = new QuizQuestionTimeState(questions.length)    
  }

  setCurrentError(error: unknown) {
    this.currentError = error
  }

  finished(level: number) {
    this.adaptivity = {
      ...this.adaptivity,
      level: { before: this.adaptivity.level.before, after: level }
    }
  }

  review(questionIndex: number, options: NavigationOptions) {
    this.updateCurrentQuestion(questionIndex, options)
  }

  goToNextQuestion(options: NavigationOptions) {
    if (!this.isNextReachable) {
      debug(`next question is not reachable`)
      return false
    }
    return this.navigation.goToNextQuestion(options)
  }

  goToPreviousQuestion(options: NavigationOptions) {
    if (!this.isPreviousReachable) {
      debug(`previous question is not reachable`)
      return false
    }    
    return this.navigation.goToPreviousQuestion(options)
  }

  enableNextQuestion() {
    const nextIndex = this.navigation.nextQuestionIndex
    if (!this.navigation.isQuestionReachable(nextIndex)) {
      return
    }

    if (!this.questionsState.isQuestionEnabled(nextIndex)) {
      this.enableQuestion(nextIndex)
    }
  }

  enableQuestion(questionIndex: number) {
    this.questionsState.enabledQuestion(questionIndex)
  }

  disableNotEvaluatedQuestions() {
    debug('disableNotEvaluatedQuestions')
    for (let index = 0; index < this.questionsState.questionCount; index++) {
      const isEvaluated = this.questionsState.isEvaluated(index)
      if (!isEvaluated) {
        debug('disabling questions', index)
        this.questionsState.disableQuestion(index)
      }
    }
  }

  updateCurrentQuestion(questionIndex: number, options: NavigationOptions) {

    debug('updateCurrentQuestion', questionIndex)
    if (!this.isQuestionReachable(questionIndex)) {
      debug(`question with index '${questionIndex}' is not reachable`)
      return
    }    
    return this.navigation.updateCurrentQuestion(questionIndex, options)
  }

  get isNextReachable() {
    return this.isQuestionReachable(this.navigation.nextQuestionIndex)
  }

  get isPreviousReachable() {
    return this.isQuestionReachable(this.navigation.previousQuestionIndex)
  }

  isQuestionReachable(questionIndex: number) {
    return this.navigation.isQuestionReachable(questionIndex) &&
          this.questionsState.isQuestionEnabled(questionIndex)
  }

  registerQuestionShownTime(time: Date) {
    const index = this.checkCurrentQuestion()
    this.questionTimes.shown(index, time)
  }

  registerQuestionAnsweredTime(time: Date) {
    const index = this.checkCurrentQuestion()
    this.questionTimes.answered(index, time)
  }

  registerQuestionFinishTime() {
    const index = this.checkCurrentQuestion()
    this.questionTimes.finished(index)
  }

  resetQuestionTime(time: Date) {
    const index = this.checkCurrentQuestion()
    this.questionTimes.reset(index, time)
  }

  queryQuestionTime(index: number) {
    return this.questionTimes.questionTime(index)
  }

  private checkCurrentQuestion() {
    if (!this.navigation.isAtQuestion) {
      throw new Error('Coding error: invalid current index: ' + this.navigation.questionIndex)
    }

    return this.navigation.questionIndex
  }

  get isInProgress() {
    return this.phaseState.isInProgress
  }

  get isInSummary() {
    return this.phaseState.isInSummary
  }

  get isInReview() {
    return this.phaseState.isInReview
  }

  get questionTexts() {
    return this.questionsState.questionTexts
  }

  get questionPhases() {
    return this.questionsState.questionPhases
  }

  get questionCount() {
    return this.questionsState.questionCount
  }

  get notAnsweredQuestionCount() {
    return this.questionsState.notAnsweredQuestionCount({ markNotEvaluatedAsNonAnswered: true })
  }

  get questionIndex() {
    return this.navigation.questionIndex
  }

  get lastVisitedIndex() {
    return this.navigation.lastVisitedIndex
  }

  get isFirstQuestion() {
    return this.navigation.isFirstQuestion
  }

  get isLastQuestion() {
    return this.navigation.isLastQuestion
  }

  get score(): Score {

    const scores = this.questionsState.questionScores
    const sum = scores.reduce((prev, cur) => {
      return { min: prev.min + cur.min,
               max: prev.max + cur.max,
               raw: prev.raw + cur.raw }
    }, { min: 0, max: 0, raw: 0 })
    
    return sum
  }

  get allPracticeQuestions() {
    return this.adaptivity.allPracticeQuestions
  }

  get practiceResult() {
    return {
      level: this.adaptivity.level
    }
  }

  questionAt(index: number) {
    return this.questionsState.question(index)
  }

  questionForStateMachine(questionIndex: number) {
    return this.questionsState.questionForStateMachine(questionIndex)
  }

  get currentQuestion(): PracticeCurrentQuestion | null {

    if (!this.navigation.isAtQuestion) {
      return null
    }

    const question = this.questionsState.question(this.navigation.questionIndex)
    const viewState = this.isInProgress ? inProgressQuestionState() : inReviewQuestionState()

    const result = {
      question: question.question,
      state: question.state,
      answer: question.answer,
      evaluationResult: {
        score: question.evaluation.details.score,
        result: question.evaluation.result
      },
      viewState: viewState
    }

    return result

    function inProgressQuestionState() {
      return {
        showEvaluate: !question.evaluated,
        canEvaluate: question.answer !== null,
        evaluated: question.evaluated,
        showSolution: question.showSolution,
        showScore: question.evaluated,
        enabled: true
      }
    }

    function inReviewQuestionState() {
      return {
        showEvaluate: false,
        canEvaluate: false,
        evaluated: question.evaluated,
        showSolution: question.evaluated,
        showScore: question.evaluated,
        enabled: false
      }
    }
  }

  get currentSessionId() {
    return this.sessionId
  }

  exportQuestionStateAt2(index: number) {
    return this.questionsState.exportState(index)
  }

  exportNavigationState() {
    return this.navigation.exportState()
  }

  exportQuestionTimesState() {
    return this.questionTimes.exportTimes()
  }

  get error() {
    return this.currentError
  }
}
